import { useMemo } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import queryClient from '@/queryClient'
import { useToast } from '@/components/ui/use-toast'

import { defaultTheme } from './defaultTheme'

import type {
  DocumentPageThemeIn,
  DocumentPageThemeTypeChoices,
} from '@/lib/api/client'

import {
  listDocumentPageThemes,
  createDocumentPageTheme,
  deleteDocumentPageTheme,
  updateDocumentPageTheme,
  listTemplateAttributes,
} from '@/lib/api/client'

import { useRelativeToAbsolutePositioning } from '@/utils/hooks/useRelativeToAbsolutePositioning'
import { useListDocumentPageThemeUserDefaults } from '../document_themes_user_defaults'
import { useJwtToken } from '@/external/onshape/hooks/useJwtToken'

import { generateThemeTemplateAttributes } from './fns'

import MUTATION_KEYS from './mutationKeys'
import QUERY_KEYS from './queryKeys'

import type { TemplateAttribute } from '@/lib/api/client'

export const useDocumentPageThemes = () => {
  const jwtToken = useJwtToken()
  return useQuery({
    queryKey: [QUERY_KEYS.DOCUMENT_THEMES],
    queryFn: async () => {
      const themes = await listDocumentPageThemes(jwtToken)
      return themes.reverse()
    },
    throwOnError: true,
  })
}

export const useDefaultDocumentThemeConfig = () => {
  const { isLoading: isThemesLoading, data: themes } = useDocumentPageThemes()
  const { isLoading: isDefaultsLoading, data: userDefaultThemes } =
    useListDocumentPageThemeUserDefaults()

  const isLoading = isThemesLoading || isDefaultsLoading

  const defaultThemeType = 'operation'

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const defaultTemplateAttributes = useMemo(() => {
    const userDefault = userDefaultThemes?.find(
      (d) => d.theme_type === defaultThemeType,
    )

    const userDefaultTheme = themes?.find(
      (t) => t.id === userDefault?.document_page_theme,
    )

    const userDefaultTemplateAttributes = userDefaultTheme?.template_attributes

    const fallbackTemplateAttributes = defaultTheme.template_attributes

    return userDefaultTemplateAttributes || fallbackTemplateAttributes
  }, [themes, userDefaultThemes]) as TemplateAttribute[]

  return {
    isLoading,
    data: {
      defaultTemplateAttributes: defaultTemplateAttributes,
    },
  }
}

export const useCreateDocumentTheme = (props?: { onCreate?: () => void }) => {
  const { toast } = useToast()
  const { relativeToAbsolutePosition } = useRelativeToAbsolutePositioning()

  return useMutation({
    mutationKey: [MUTATION_KEYS.CREATE_DOCUMENT_THEME],
    mutationFn: async ({
      name,
      theme_type,
      documentPageId,
    }: {
      name: string
      theme_type: DocumentPageThemeTypeChoices
      documentPageId: string
    }) => {
      const templateAttributes = await listTemplateAttributes(documentPageId)

      const absolutePositionedTemplateAttributes = templateAttributes.map(
        (templateAttribute) => {
          const isImage =
            templateAttribute.data_type === 'view' ||
            templateAttribute.data_type === 'image'
          const isNote = templateAttribute.data_type === 'note'

          if (isImage) {
            const isRelative =
              templateAttribute.template_values.imagePosition === 'relative'
            const position = isRelative
              ? relativeToAbsolutePosition(templateAttribute)
              : templateAttribute.template_values.position
            return {
              ...templateAttribute,
              template_values: {
                ...(templateAttribute.template_values as any),
                imagePosition: 'absolute',
                position: position || { x: 0, y: 0 },
              },
            } as TemplateAttribute
          }

          if (isNote) {
            const isRelative =
              templateAttribute.template_values.notesPosition === 'relative'
            const position = isRelative
              ? relativeToAbsolutePosition(templateAttribute)
              : templateAttribute.template_values.position

            return {
              ...templateAttribute,
              template_values: {
                ...(templateAttribute.template_values as any),
                notesPosition: 'absolute',
                position: position || { x: 0, y: 0 },
              },
            } as TemplateAttribute
          }

          return templateAttribute
        },
      )

      const themeTemplateAttributes = await generateThemeTemplateAttributes({
        templateAttributes: absolutePositionedTemplateAttributes
          // avoid applying themes to sub pages
          .filter(
            (attr) =>
              !attr.template_values?.subPageId ||
              attr.template_values?.subPageId === documentPageId,
          ),
      })

      await createDocumentPageTheme({
        name,
        theme_type,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        template_attributes: themeTemplateAttributes
          // avoid applying themes to sub pages
          .filter(
            (attr) =>
              !attr.template_values?.subPageId ||
              attr.template_values?.subPageId === documentPageId,
          ) as any,
      })

      return themeTemplateAttributes
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.DOCUMENT_THEMES],
        refetchType: 'all',
      })

      if (props?.onCreate && typeof props.onCreate === 'function') {
        props.onCreate()
      }
    },
    onError: (error) => {
      console.error(error)
      toast({
        variant: 'destructive',
        title: 'Uh oh! Something went wrong.',
        description: 'There was a problem with your request.',
      })
    },
  })
}

export const useDeleteDocumentTheme = () => {
  return useMutation({
    mutationKey: [MUTATION_KEYS.DELETE_DOCUMENT_THEME],
    mutationFn: async (documentPageThemeId: string) => {
      return deleteDocumentPageTheme(documentPageThemeId)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.DOCUMENT_THEMES],
        refetchType: 'all',
      })
    },
  })
}

export const useUpdateDocumentTheme = () => {
  const { toast } = useToast()
  const { relativeToAbsolutePosition } = useRelativeToAbsolutePositioning()

  return useMutation({
    mutationKey: [MUTATION_KEYS.UPDATE_DOCUMENT_THEME],
    mutationFn: async ({
      documentPageId,
      documentPageThemeId,
      ...values
    }: Omit<DocumentPageThemeIn, 'template_attributes'> & {
      documentPageId: string
      documentPageThemeId: string
    }) => {
      const templateAttributes = await listTemplateAttributes(documentPageId)

      const absolutePositionedTemplateAttributes = templateAttributes.map(
        (templateAttribute) => {
          const isImage =
            templateAttribute.data_type === 'view' ||
            templateAttribute.data_type === 'image'
          const isNote = templateAttribute.data_type === 'note'

          if (isImage) {
            const isRelative =
              templateAttribute.template_values.imagePosition === 'relative'
            const position = isRelative
              ? relativeToAbsolutePosition(templateAttribute)
              : templateAttribute.template_values.position
            return {
              ...templateAttribute,
              template_values: {
                ...(templateAttribute.template_values as any),
                imagePosition: 'absolute',
                position: position || { x: 0, y: 0 },
              },
            } as TemplateAttribute
          }

          if (isNote) {
            const isRelative =
              templateAttribute.template_values.notesPosition === 'relative'
            const position = isRelative
              ? relativeToAbsolutePosition(templateAttribute)
              : templateAttribute.template_values.position

            return {
              ...templateAttribute,
              template_values: {
                ...(templateAttribute.template_values as any),
                notesPosition: 'absolute',
                position: position || { x: 0, y: 0 },
              },
            } as TemplateAttribute
          }

          return templateAttribute
        },
      )

      const themeTemplateAttributes = await generateThemeTemplateAttributes({
        templateAttributes: absolutePositionedTemplateAttributes,
      })

      return updateDocumentPageTheme(documentPageThemeId, {
        ...values,
        template_attributes: themeTemplateAttributes as any,
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.DOCUMENT_THEMES],
        refetchType: 'all',
      })
    },
    onError: (error) => {
      console.error(error)
      toast({
        variant: 'destructive',
        title: 'Uh oh! Something went wrong.',
        description: 'There was a problem with your request.',
      })
    },
  })
}
